import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shared/components/ui/tabs";
import {
  formatOutputTypeLabel,
  normalizeOutputTypes,
  outputTypeValues,
  type OutputTypeEnum,
} from "@/domains/templates/output-types";
import type { UiTemplateSection } from "@/domains/templates/types";
import { TemplateMarkdown } from "./template-markdown";

type StepMetadataForm = {
  name: string;
  description: string;
  outputTypes: OutputTypeEnum[];
};

type TemplateStepMetadataDialogProps = {
  open: boolean;
  section: UiTemplateSection;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (values: StepMetadataForm) => Promise<unknown>;
};

export function TemplateStepMetadataDialog({
  open,
  section,
  isPending,
  onOpenChange,
  onSave,
}: TemplateStepMetadataDialogProps) {
  const [error, setError] = useState<string>();
  const { register, watch, setValue, reset, handleSubmit } =
    useForm<StepMetadataForm>({
      defaultValues: {
        name: section.name,
        description: section.description ?? "",
        outputTypes: section.outputTypes,
      },
    });
  const outputTypes = normalizeOutputTypes(watch("outputTypes"));
  const descriptionDraft = watch("description");

  useEffect(() => {
    if (!open) return;
    reset({
      name: section.name,
      description: section.description ?? "",
      outputTypes: section.outputTypes,
    });
    setError(undefined);
  }, [open, reset, section]);

  const submit = handleSubmit(async (values) => {
    setError(undefined);
    try {
      await onSave({
        ...values,
        name: values.name.trim(),
        description: values.description.trim(),
        outputTypes:
          normalizeOutputTypes(values.outputTypes).length > 0
            ? normalizeOutputTypes(values.outputTypes)
            : ["markdown"],
      });
      onOpenChange(false);
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Could not save section",
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit section</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <label className="grid gap-1 text-sm font-medium">
            Section title
            <Input
              aria-label="Section title"
              {...register("name", { required: true })}
            />
          </label>
          <div className="grid gap-1 text-sm font-medium">
            <p>Section description</p>
            <Tabs defaultValue="write" className="w-full">
              <TabsList>
                <TabsTrigger value="write">Write</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
              <TabsContent value="write">
                <Textarea
                  aria-label="Section description"
                  className="max-h-[40dvh] resize-y overflow-y-auto"
                  {...register("description")}
                />
              </TabsContent>
              <TabsContent value="preview">
                <TemplateMarkdown
                  content={descriptionDraft}
                  emptyText="Nothing to preview yet."
                  className="max-h-[40dvh] min-h-16 overflow-y-auto rounded-md border border-input px-3 py-2"
                />
              </TabsContent>
            </Tabs>
          </div>
          <div className="grid gap-1 text-sm font-medium">
            Output types
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="justify-between"
                >
                  {outputTypes.map(formatOutputTypeLabel).join(", ")}
                  <span>{outputTypes.length}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Select output types</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {outputTypeValues.map((value) => {
                  const checked = outputTypes.includes(value);
                  return (
                    <DropdownMenuCheckboxItem
                      key={value}
                      checked={checked}
                      disabled={checked && outputTypes.length === 1}
                      onCheckedChange={(nextChecked) => {
                        const next = nextChecked
                          ? [...new Set([...outputTypes, value])]
                          : outputTypes.filter((item) => item !== value);
                        setValue("outputTypes", next, { shouldDirty: true });
                      }}
                      onSelect={(event) => event.preventDefault()}
                    >
                      {formatOutputTypeLabel(value)}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isPending}
            onClick={() => void submit()}
          >
            {isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
